import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type BucketSearchResult = {
  identifier: string;
  type: string;
  url: string;
  title: string;
  prompt: string;
  describe: string;
  description: string;
  paragraphs: { tokens: number; heading: string; content: string }[];
  override: string;
  body?: string;
  total_tokens: number;
  matched: TelnyxBucketMatch;
};

export type TelnyxBucketMatch = {
  chunk: {
    bucket?: string;
    index?: string;
    heading: string | null;
    content: string;
    certainty: number;
    tokens: number;
  };
  identifier: string;
  loader_metadata: any;
  loader_type: string;
  type: string;
};

type BucketSearch = {
  query: string;
  indexes: any[];
  results: BucketSearchResult[];
  loading: boolean;
  max_results: number;
  min_certainty: number;
};

export type PaginationState = {
  page: number;
  pageSize: number;
};

export type bucketTypes = {
  search: BucketSearch;
  task_id: string | null;
  pagination: PaginationState;
};

const initialState: bucketTypes = {
  search: {
    query: '',
    indexes: [],
    results: [],
    loading: false,
    max_results: 3,
    min_certainty: 70,
  },
  task_id: null,
  pagination: {
    page: 0,
    pageSize: 15,
  },
};

const bucketSlice = createSlice({
  name: 'buckets',
  initialState,
  reducers: {
    reset: (state) => {
      state.search.query = '';
      state.search.results = [];
    },
    setBucketSearch: (
      state,
      action: PayloadAction<{ field: string; value: string | number; id?: string; type?: string | null }>
    ) => {
      const { field, value } = action.payload;

      if (field === 'query') state.search = { ...state.search, query: value as string };

      if (field === 'index' && action.payload.id && action.payload.type) {
        state.search = {
          ...state.search,
          indexes: state.search.indexes.map((idx) => {
            if (idx.id === action.payload.id) {
              if (action.payload.type) return { ...idx, [field]: value as string, type: action.payload.type };
              return { ...idx, [field]: value as string };
            }

            return idx;
          }),
        };
      }

      if (field === 'weight' && action.payload.id) {
        state.search = {
          ...state.search,
          indexes: state.search.indexes.map((idx) => {
            if (idx.id === action.payload.id) return { ...idx, weight: action.payload.value as number };
            return idx;
          }),
        };
      }

      if (field === 'indexes' && action.payload.id && action.payload.type) {
        state.search = {
          ...state.search,
          indexes: state.search.indexes.map((idx) => {
            if (idx.id === action.payload.id) {
              return { ...idx, index: value as string, type: action.payload.type };
            }

            return idx;
          }),
        };
      }
    },
    setTaskId: (state, action: PayloadAction<string | null>) => {
      state.task_id = action.payload;
    },
    setBucketSearchResults: (
      state,
      action: PayloadAction<{ field: string; value: string | number; id?: string; type?: string | null }>
    ) => {
      const { field, value } = action.payload;

      if (field === 'query') state.search = { ...state.search, query: value as string };
      if (field === 'index' && action.payload.id && action.payload.type) {
        state.search = {
          ...state.search,
          indexes: state.search.indexes.map((idx) => {
            if (idx.id === action.payload.id) {
              if (action.payload.type) return { ...idx, [field]: value as string, type: action.payload.type };
              return { ...idx, [field]: value as string };
            }

            return idx;
          }),
        };
      }

      if (field === 'weight' && action.payload.id) {
        state.search = {
          ...state.search,
          indexes: state.search.indexes.map((idx) => {
            if (idx.id === action.payload.id) return { ...idx, weight: action.payload.value as number };
            return idx;
          }),
        };
      }

      if (field === 'indexes' && action.payload.id && action.payload.type) {
        state.search = {
          ...state.search,
          indexes: state.search.indexes.map((idx) => {
            if (idx.id === action.payload.id) {
              return { ...idx, index: value as string, type: action.payload.type };
            }

            return idx;
          }),
        };
      }
    },
    setSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.search.loading = action.payload;
    },
    setMaxResults: (state, action: PayloadAction<number>) => {
      state.search.max_results = action.payload;
    },
    setMinCertainty: (state, action: PayloadAction<number>) => {
      state.search.min_certainty = action.payload;
    },
    storeBucketSearchResults: (state, action: PayloadAction<any>) => {
      const data = action.payload;
      state.search.results = data;
    },
    addBucketSearchIndex: (state, action: PayloadAction<any>) => {
      state.search.indexes = state.search.indexes.concat(action.payload);
    },
    removeBucketSearchIndex: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.search.indexes = state.search.indexes.filter((idx) => idx.id !== id);
    },
    setPaginationState: (state, action: PayloadAction<PaginationState>) => {
      state.pagination = action.payload;
    },
  },
});

export const bucketActions = bucketSlice.actions;
export default bucketSlice.reducer;
